/**
 * Owner: Masa Bayu
 * File: catalog-port.ts
 * Date:Time: 2026-10-22 23:52
 * Description: PW-A catalog read port — shared Home · Detail · Search/Discover.
 *   §466 S1: sumber lalai ialah qxp-hub (port lalai kosong; overlay hub mengisi).
 */

import type { QxreadCatalogSerialized } from '@qx/qxread-core';

/** Thin catalog read API — swap adapter without changing segment UIs. */
export interface QxreadCatalogPort {
  readonly listCatalog: () => readonly QxreadCatalogSerialized[];
  readonly getCatalogEntry: (publicationId: string) => QxreadCatalogSerialized | null;
}

// §466 S1 — qxp-hub ialah satu-satunya sumber katalog. Port lalai sengaja KOSONG:
// rak hanya diisi oleh overlay hub (setHubCatalogOverlay selepas sync /v1/catalog)
// atau port host yang diikat jelas. Tiada lagi katalog demo/produksi setempat.
const emptyPort: QxreadCatalogPort = Object.freeze({
  listCatalog: () => Object.freeze([]),
  getCatalogEntry: () => null,
});

let activePort: QxreadCatalogPort = emptyPort;

// Versi catalog — bertambah setiap kali sumber catalog bertukar (port bind/reset,
// overlay hub sync). Skrin yang membaca listCatalog() pada render (cth. Home
// marketplace) melanggan melalui useSyncExternalStore supaya terbitan hub yang
// tiba selepas render pertama (sync asinkron) muncul tanpa muat semula penuh.
let catalogVersion = 0;
const catalogListeners = new Set<() => void>();

export function getCatalogVersion(): number {
  return catalogVersion;
}

export function subscribeCatalog(listener: () => void): () => void {
  catalogListeners.add(listener);
  return () => {
    catalogListeners.delete(listener);
  };
}

function bumpCatalogVersion(): void {
  catalogVersion += 1;
  for (const listener of catalogListeners) listener();
}

/** Bind catalog adapter (lalai: port kosong — hanya hub/host yang mengikat). */
export function setCatalogPort(port: QxreadCatalogPort): void {
  activePort = port;
  bumpCatalogVersion();
}

/** Return to the hub-only default (kosong sehingga overlay/host mengisi). */
export function resetCatalogPort(): void {
  activePort = emptyPort;
  bumpCatalogVersion();
}

// Fasa 3 — overlay hub (syncQxpHub): terbitan yang disorong ke qxp-hub muncul di
// catalog secara dinamik. Selepas §466 S1 overlay ini ialah SATU-SATUNYA sumber
// rak; overlay kosong = rak kosong (fail-closed, bukan data rekaan).
let hubOverlayEntries: readonly QxreadCatalogSerialized[] = Object.freeze([]);

/** Gantikan overlay hub (panggil dengan hasil sync catalog hub; kosongkan untuk reset). */
export function setHubCatalogOverlay(entries: readonly QxreadCatalogSerialized[]): void {
  hubOverlayEntries = Object.freeze([...entries]);
  bumpCatalogVersion();
}

/** Ujian sahaja. */
export function __clearHubCatalogOverlayForTests(): void {
  hubOverlayEntries = Object.freeze([]);
  bumpCatalogVersion();
}

function mergedCatalog(): readonly QxreadCatalogSerialized[] {
  if (hubOverlayEntries.length === 0) return activePort.listCatalog();
  const byId = new Map<string, QxreadCatalogSerialized>();
  for (const entry of activePort.listCatalog()) {
    byId.set(entry.publicationId, entry);
  }
  for (const entry of hubOverlayEntries) {
    byId.set(entry.publicationId, entry);
  }
  return Object.freeze([...byId.values()]);
}

/** List catalog entries through the active port. */
export function listCatalog(): readonly QxreadCatalogSerialized[] {
  return mergedCatalog();
}

/** Resolve one catalog entry through the active port. */
export function getCatalogEntry(publicationId: string): QxreadCatalogSerialized | null {
  const id = publicationId.trim();
  const hubEntry = hubOverlayEntries.find((entry) => entry.publicationId === id);
  if (hubEntry !== undefined) return hubEntry;
  return activePort.getCatalogEntry(id);
}
